/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { cx, css } from '@emotion/css';
import ReactSelect from 'react-select';
import Creatable from 'react-select/creatable';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createBranchTheme, Typography } from '../../../electrons';
import wrappedComponents from './Components';
import { getClearable } from './Compat';
import { makeCustomStyles } from './Dropdown.styles';
const REQUIRED_FOOTNOTE = 'This field is required.';
/**
 * For all property options, see: https://github.com/JedWatson/react-select
 *
 * Props that we want to handle instead of directly passing into react-select:
 */
export const HANDLED_PROPS = [
    'components',
    'disabled',
    'testid',
    'onChange',
    'onOpen',
    'i13nLabel',
    'outerClassName',
    'label',
    'labelClassName',
    'requiredNote',
    'childRef',
    'backspaceRemovesValue',
    'styles',
];
export function findValueInOptions(value, options) {
    const foundValues = options === null || options === void 0 ? void 0 : options.filter(Boolean).map((option) => {
        if (option.options) {
            return findValueInOptions(value, option.options);
        }
        return option.value === value ? option : undefined;
    });
    return foundValues.find(Boolean);
}
export const getValue = (options, value) => {
    if (value === undefined) {
        return value;
    }
    if (value === null) {
        return null;
    }
    if (Array.isArray(value)) {
        return value.map((single) => getValue(options, single)).filter(Boolean);
    }
    if (value instanceof Object) {
        return value;
    }
    return findValueInOptions(value, options);
};
export const DropdownContext = React.createContext(undefined);
/**
 * @deprecated
 */
export class Dropdown extends React.PureComponent {
    constructor(props) {
        super(props);
        /*
         * For regular dropdowns, e will be type: SelectEvent obj.
         * If dropdown is a multiselect, e will be type: an array of SelectEvent objs.
         */
        this.fireMixpanelEvent = (type, e) => {
            if (!this.context.i13n) {
                return;
            }
            // Set defaults
            let i13nModel = this.props.i13nModel || {};
            const i13nLabel = i13nModel.label || this.props.i13nLabel || '';
            // Add "- Dropdown open" or "- Dropdown change" to the label
            const label = `${i13nLabel} - Dropdown ${type}`;
            // Merge params
            const params = Object.assign({ component: 'Select', option: e || {} }, i13nModel.params);
            // Final merge incase there are other properties passed besides label and params
            i13nModel = Object.assign(Object.assign({}, i13nModel), { label, params });
            this.context.i13n.executeEvent('log', i13nModel);
        };
        this._onOpen = () => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            this.props.onOpen && this.props.onOpen();
            this.fireMixpanelEvent('open');
        };
        /*
         * For regular dropdowns, e will be type: SelectEvent obj.
         * If dropdown is a multiselect, e will be type: an array of SelectEvent objs.
         */
        this._onChange = (e, action) => {
            this.props.onChange(e, action);
            this.fireMixpanelEvent('change', e);
        };
        if (!props.i13nLabel && !props.i13nModel) {
            // eslint-disable-next-line no-console
            console.warn('One of i13nLabel or i13nModel must be passed to the Dropdown component.');
        }
    }
    render() {
        const { props } = this;
        const Component = props.creatable ? Creatable : ReactSelect;
        const childProps = {};
        const theme = createBranchTheme();
        const keys = Object.keys(props);
        for (let i = 0; i < keys.length; i += 1) {
            const key = keys[i];
            if (!HANDLED_PROPS[key]) {
                childProps[key] = props[key];
            }
        }
        if (props.creatable && props.childRef) {
            const { childRef = {} } = props;
            // paste requires a reference to react-select component internals
            // eslint-disable-next-line no-return-assign
            childProps.ref = (ref) => (childRef.pasteInput = ref);
        }
        const getOption = () => {
            const { optionRenderer } = props;
            if (optionRenderer) {
                return (optionProps) => {
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    const { cx, getStyles } = optionProps;
                    const className = cx(css(getStyles('option', optionProps)));
                    return (React.createElement("div", Object.assign({ ref: optionProps.innerRef, className: className }, optionProps.innerProps), optionRenderer(optionProps.data)));
                };
            }
            if (props.components && props.components.Option) {
                return props.components.Option;
            }
            return (optionProps) => (React.createElement(wrappedComponents.Option, Object.assign({}, optionProps)));
        };
        const className = cx(props.className, 'Select', props.multi ? 'Select--multi' : 'Select--single', props.disabled ? 'is-disabled' : undefined);
        const styles = Object.assign(Object.assign({}, makeCustomStyles(theme, props.warning, props.requiredNote)), (props.styles || {}));
        return (React.createElement("div", { className: cx(props.outerClassName), "data-testid": props.id },
            React.createElement(Component
            // eslint-disable-next-line react/jsx-props-no-spreading
            , Object.assign({}, childProps, { createOptionPosition: props.creatable && 'first', isSearchable: props.searchable, styles: styles, className: className, classNamePrefix: "Select", backspaceRemovesValue: props.backspaceRemoves, isDisabled: props.disabled, value: getValue(props.options, props.value), components: Object.assign(Object.assign(Object.assign({}, wrappedComponents), (this.props.components || {})), { Option: getOption() }), isClearable: getClearable(props), isMulti: props.multi, isOptionDisabled: (option) => option.disabled, closeMenuOnSelect: props.closeMenuOnSelect || !props.multi, 
                // eslint-disable-next-line no-underscore-dangle
                onChange: this._onChange })),
            props.requiredNote && (React.createElement("div", { className: "input-control__note h_ta-start", style: styles.errorMessageContainer() },
                React.createElement(InfoOutlinedIcon, { style: styles.infoIcon() }),
                React.createElement(Typography, { variant: "body2" }, REQUIRED_FOOTNOTE)))));
    }
}
Dropdown.contextType = DropdownContext;
//# sourceMappingURL=Dropdown.js.map